<!--
Renders control elements for support & wellbeing 
Calls API to get list of options. Uses modal component to display list for user to select.
When a selection is made, the API is called again to get the content or render another list.
@author dgm11b
-->

<template>
    <div 
        class="container px-3 pt-2 pb-0"
    >
        <template 
            v-for="(dropdown, index) in selectText"
        >
            <div 
                class="d-flex"
            >
                <div 
                    v-if="index !== 1 || showAdditionalSelect"
                    class="flex-grow-1"
                >
                    <p :id="'selectLabel' + index" class="fw-bold blue-text">{{ modalTitle[index] }}</p>
                </div>
                <button 
                    v-if="index !== 1"
                    class="clear-btn blue-text fw-bold pe-2 mb-3"
                    @click="clearSelection"
                >
                    Clear
                </button>
            </div>
            <button 
                v-if="index !== 1 || showAdditionalSelect"
                :id="'selectButton' + index"
                class="sw-select blue-text"
                @click="toggleModal(index)"
                :aria-labelledby="'selectLabel' + index"
            >
                {{ selectText[index] }}
        </button>
        </template>
        <NewModalPopup 
            v-if="showModal"
            showX="true"
            :title="modalTitle[selectTrigger]"
            id="swModal"
            @close="toggleModal()"
        >
            <ul
                role="listbox"
            >
                <li 
                    v-for="(option, index) in selectOptions[selectTrigger]"
                    :key="index+'swcontent'"
                    :class="[
                        'sw-option',
                        index === selectedOption[selectTrigger] ? 'selected' : null
                    ]"
                    :handle="option.handle"
                    :aria-selected="index === selectedOption[selectTrigger] ? 'true' : null"
                    @click="selected($event, index)"
                    @keyup.enter="selected($event, index)"
                    role="option"
                    tabindex="0"
                >
                    {{ option.content }}
                </li>
            </ul>
        </NewModalPopup>
    </div>
</template>


<script setup>
import { ref, defineEmits, onMounted, onUnmounted } from 'vue';
import NewModalPopup from '@/components/NewModalPopup.vue';
import axios from 'axios'
import {fireAnalyticsSetup} from '@/composables/FirebaseAnalytics.js'

const {swListener} = fireAnalyticsSetup()

const selectOptions = ref([])
const selectText = ref(['', ''])
const selectedOption = ref([null, null])
const showAdditionalSelect = ref(false)
const selectTrigger = ref(null)
const showModal = ref(false)
const modalTitle = ref(["How can we help?", "What resources would help?"])


const emit = defineEmits(['selection'])


function clearSelection() {
    showAdditionalSelect.value = false
    document.querySelector('.sw-select').innerHTML = '';

    //Clear text in options button and selected option in option list
    for (let i = 0; i < selectText.value.length; i++) {
        selectText.value[i] = ''  
        selectedOption.value[i] = null 
    }

    emit('selection', "")
}

function toggleModal(select = null) {
    
    if(select !== null) {
        selectTrigger.value = select
    }

    showModal.value = !showModal.value

    //Return focus back to select button once modal closed
    if(selectTrigger.value !== null && showModal.value === false) {
        document.getElementById('selectButton'+selectTrigger.value).focus();
    }
}

function selected(e, selectIndex) {

    let handle = e.target.getAttribute("handle")

    //Set text for select element
    selectText.value[selectTrigger.value] = e.target.innerText;

    //Get the endpoint handle
    //Make api call to the /next/ endpoint
    //If the response contains 'options' then display other dropdown
    //Else display the content
    getNext(handle).then(response => {

        if(response.data.options.length > 0) {

            //Add options to second select box
            //Not using 'push' because this action could be performed multiple times
            //So don't want to push to the array each time
            selectOptions.value[1] = response.data.options
            //Clear content
            emit('selection', "")
            showAdditionalSelect.value = true

        } else if(response.data.sites.length > 0) {

            let content = []
            
            for(const site of response.data.sites) {
                content.push(site.pageContent)
            }

            //update content
            emit('selection', content)

            //If selection made from dropdown 1
            //Hide dropdown 2 and clear its display text
            if(selectTrigger.value === 0) {
                showAdditionalSelect.value = false
                selectText.value[1] = '';
            }

        }
        
        toggleModal()

        //Set the selection index to show user current select option when re-opening options
        selectedOption.value[selectTrigger.value] = selectIndex;
    })
   
}

async function getNext(selection) {

    return await axios.get('/timetable/wellbeing/next/'+selection)

}

onMounted(() => {
        //Get options
        axios.get('/timetable/wellbeing/start')
        .then(response => {
            selectOptions.value.push(response.data)
        })
        //Add event listener for dynamic sw options
        swListener('add')
    }
) 

onUnmounted(() => {
        //Remove event listener for sw options or it will duplicate if user returns to this page and this component gets mounted again
        swListener('remove')
    }
)

</script>

<style>
/*global styles*/

:root {
    --uog-dark-blue: #003865;
}

/* Targeting child model */
#swModal .modal-container{
    width: 90%;
    max-width: 800px;
    padding: 0px;
    overflow: auto;
}

#swModal .modal-header {
    background-color: #fff;
    color: var(--uog-dark-blue);
    padding-left: 20px;
}

#swModal .modal-container,
#swModal .modal-header {
    border-radius:8px;
}

#swModal .modal-body {
    /*padding:0px 20px;*/
    padding:0px;
    overflow-y: auto;
    max-height: calc(100vh - 200px);
    margin-bottom: 40px;
}

#swModal .sw-option {
    cursor:pointer;
}

#swModal .sw-option:hover {
    background-color: var(--uog-dark-blue);
    color:#fff;
}

</style>

<style scoped>

.clear-btn {
    cursor:pointer;
    background: transparent;
    border: none;
    padding: 0px;
    text-decoration: underline;
}

.clear-btn:hover {
    text-decoration: none;
}

.sw-select {
    width: 100%;
    border-radius: 8px;
    border: 2px solid rgba(0, 56, 101, 0.25);
    background: #FFF;
    height: 44px;
    padding: 10px 50px 10px 18px;
    margin-bottom: 15px;
    font-size: 14px;
    position: relative;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-align:left;
}

.sw-select::after {
    display:block;
    content: '';
    background-image:url('@/assets/br_down.svg');
    background-repeat: no-repeat;
    background-size: cover;
    position: absolute;
    height: 13px;
    width: 20px;
    top: 50%;
    right: 15px;
    transform: translateY(-50%);
}
    
#swModal ul {
    list-style-type: none;
    padding-left: 0px;
}

.sw-option {
    border-bottom: solid 1px rgba(0, 0, 0, 0.10);
    padding: 8px 24px;
}

.sw-option:last-child {
    border-bottom: 0px;
}

.sw-option.selected {
    border-left: 10px solid var(--uog-dark-blue);
    font-weight:bold;
    padding-left: 14px;
}

</style>